import { classifyScannedCode } from './label-code.js';

/**
 * Laufzettel (Plan Welle 3, N-A): Eine Helferin erfasst die Etiketten im Korb schon in der Schlange, das Handy zeigt die
 * Codes als QR, die Kasse übernimmt den Korb mit einem Scan. Rein lokal, kein Server-Weg. Der QR enthält nur Codes: keine
 * Preise, keine Personendaten.
 *
 * Format (ein Teil je QR):
 *
 *     BARSOVK1 <BASAR8> <KENNUNG> <teil>.<teile> <anzahl> <code> <code> … <prüfwert>
 *
 * - `BARSOVK1`: Vorab-Kasse, Version 1. Wie die Kennung ohne Y und Z: Handscanner mit US-Tastaturbelegung vertauschen sie
 *   an einem deutschen Rechner, ein Laufzettel kann so auch über das Handscanner-Feld kommen.
 * - `BASAR8`: erste 8 Hex-Zeichen der Basar-ID in Großbuchstaben. Ein Laufzettel eines anderen Basars wird abgewiesen.
 * - `KENNUNG`: 5 Zufallszeichen, gehört zu genau einem Laufzettel. Die Kasse führt nur Teile gleicher Kennung zusammen.
 * - `anzahl`: Zahl der Codes in diesem Teil, wird gegen die gelesenen Codes geprüft.
 * - Trenner ist das Leerzeichen: Es kommt in keinem gültigen Code vor (`isExternalCode`).
 * - `prüfwert`: 8 Hex-Zeichen (groß), die ersten vier Bytes von SHA-256 über den ganzen Text davor (ohne das letzte
 *   Leerzeichen). Ein beschädigter oder abgeschnittener Text ergibt `null`, nie eine halbe Liste.
 * - Zeichenbudget je QR: höchstens 40 Codes UND höchstens 500 Zeichen, sonst beginnt ein neuer Teil („Teil 1/2“).
 *
 * Jeder Code läuft durch `classifyScannedCode` (CLAUDE.md Regel 4): Was die Funktion nicht als BARSO- oder fremden Code
 * erkennt, kommt nie in einen Laufzettel und nie aus ihm heraus. Der Laufzettel ist nicht signiert (Restrisiko: Hilfe).
 */

export const RUN_SHEET_PREFIX = 'BARSOVK1';
export const RUN_SHEET_MAX_CODES = 40;
export const RUN_SHEET_MAX_CHARS = 500;
/** Höchstens so viele Teile je Laufzettel (eine Ziffer, damit die Kopfzeile fest lang bleibt). */
export const RUN_SHEET_MAX_PARTS = 9;
export const RUN_SHEET_ID_LENGTH = 5;
/** Ohne I, O, 0, 1 (Verwechslung) und ohne Y, Z (US/DE-Tastaturbelegung des Handscanners). */
export const RUN_SHEET_ID_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWX23456789';

/** Liefert eine gleichverteilte ganze Zahl von 0 bis `max - 1`. Die Zufallsquelle wird übergeben (Browser: `crypto`). */
export type RandomIndex = (max: number) => number;

const ID_PATTERN = new RegExp(`^[${RUN_SHEET_ID_ALPHABET}]{${String(RUN_SHEET_ID_LENGTH)}}$`);
const EVENT_KEY_PATTERN = /^[0-9A-F]{8}$/;
const CHECK_PATTERN = /^[0-9A-F]{8}$/;

// --- SHA-256 (rein, synchron: läuft in Browser, App und Node gleich; der Test vergleicht mit `node:crypto`) -------------

function firstPrimes(count: number): number[] {
  const primes: number[] = [];
  for (let candidate = 2; primes.length < count; candidate += 1) {
    if (primes.every((prime) => candidate % prime !== 0)) primes.push(candidate);
  }
  return primes;
}

const FRACTION = 4294967296;
const PRIMES = firstPrimes(64);
export const SHA256_ROUND_CONSTANTS = PRIMES.map((prime) =>
  Math.floor((Math.cbrt(prime) % 1) * FRACTION),
);
export const SHA256_INITIAL_HASH = PRIMES.slice(0, 8).map((prime) =>
  Math.floor((Math.sqrt(prime) % 1) * FRACTION),
);

const rotr = (value: number, bits: number): number => (value >>> bits) | (value << (32 - bits));

/** SHA-256 eines reinen ASCII-Textes als 64 Hex-Zeichen (Kleinbuchstaben). */
export function sha256Hex(text: string): string {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code > 127) throw new RangeError('sha256Hex rechnet nur über ASCII-Text.');
    bytes.push(code);
  }
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const high = Math.floor(bitLength / FRACTION);
  const low = bitLength >>> 0;
  for (const word of [high, low]) {
    bytes.push((word >>> 24) & 0xff, (word >>> 16) & 0xff, (word >>> 8) & 0xff, word & 0xff);
  }
  const hash = [...SHA256_INITIAL_HASH];
  const w = new Array<number>(64).fill(0);
  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let t = 0; t < 16; t += 1) {
      const at = offset + t * 4;
      w[t] =
        (((bytes[at] ?? 0) << 24) |
          ((bytes[at + 1] ?? 0) << 16) |
          ((bytes[at + 2] ?? 0) << 8) |
          (bytes[at + 3] ?? 0)) >>>
        0;
    }
    for (let t = 16; t < 64; t += 1) {
      const w15 = w[t - 15] ?? 0;
      const w2 = w[t - 2] ?? 0;
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
      w[t] = ((w[t - 16] ?? 0) + s0 + (w[t - 7] ?? 0) + s1) >>> 0;
    }
    let [a = 0, b = 0, c = 0, d = 0, e = 0, f = 0, g = 0, h = 0] = hash;
    for (let t = 0; t < 64; t += 1) {
      const sum1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 = (h + sum1 + choice + (SHA256_ROUND_CONSTANTS[t] ?? 0) + (w[t] ?? 0)) >>> 0;
      const sum0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sum0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    const next = [a, b, c, d, e, f, g, h];
    for (let i = 0; i < 8; i += 1) hash[i] = ((hash[i] ?? 0) + (next[i] ?? 0)) >>> 0;
  }
  return hash.map((word) => word.toString(16).padStart(8, '0')).join('');
}

/** Prüfwert eines Laufzettel-Teils: erste 8 Hex-Zeichen (groß) von SHA-256 über den Text davor. */
export function runSheetChecksum(body: string): string {
  return sha256Hex(body).slice(0, 8).toUpperCase();
}

// --- Kennung und Basar-Kürzel ------------------------------------------------------------------------------------------

/** Neue Kennung für einen Laufzettel (5 Zeichen aus `RUN_SHEET_ID_ALPHABET`). Zufallsquelle wird übergeben. */
export function createRunSheetId(randomIndex: RandomIndex): string {
  let id = '';
  for (let i = 0; i < RUN_SHEET_ID_LENGTH; i += 1) {
    const index = randomIndex(RUN_SHEET_ID_ALPHABET.length);
    if (!Number.isInteger(index) || index < 0 || index >= RUN_SHEET_ID_ALPHABET.length) {
      throw new RangeError('randomIndex muss eine ganze Zahl im geforderten Bereich liefern.');
    }
    id += RUN_SHEET_ID_ALPHABET.charAt(index);
  }
  return id;
}

export function isRunSheetId(value: string): boolean {
  return ID_PATTERN.test(value);
}

/** Kürzel des Basars im Laufzettel: erste 8 Hex-Zeichen der Basar-ID in Großbuchstaben; `null`, wenn die ID keine UUID ist. */
export function runSheetEventKey(eventId: string): string | null {
  const key = eventId.slice(0, 8).toUpperCase();
  return EVENT_KEY_PATTERN.test(key) ? key : null;
}

// --- Kodieren ----------------------------------------------------------------------------------------------------------

export class RunSheetError extends Error {
  constructor(
    readonly reason: 'event' | 'id' | 'empty' | 'code' | 'duplicate' | 'too_many',
    message: string,
  ) {
    super(message);
    this.name = 'RunSheetError';
  }
}

function partText(
  eventKey: string,
  id: string,
  part: number,
  parts: number,
  codes: readonly string[],
): string {
  const body = [
    RUN_SHEET_PREFIX,
    eventKey,
    id,
    `${String(part)}.${String(parts)}`,
    String(codes.length),
    ...codes,
  ].join(' ');
  return `${body} ${runSheetChecksum(body)}`;
}

/** Zeichenzahl eines Teils mit diesen Codes (Teilnummer und -zahl haben je eine Ziffer, `anzahl` höchstens zwei). */
function partLength(codes: readonly string[]): number {
  return partText('00000000', 'AAAAA', 1, 1, codes).length;
}

/**
 * Zerlegt die Codes eines Korbs in Teile nach dem Zeichenbudget (höchstens 40 Codes und 500 Zeichen je Teil) und gibt den
 * Text je QR zurück. Jeder Code muss `classifyScannedCode` bestehen, ein Code zweimal ist ein Fehler. Die Reihenfolge bleibt.
 */
export function encodeRunSheet(input: {
  eventId: string;
  id: string;
  codes: readonly string[];
}): string[] {
  const eventKey = runSheetEventKey(input.eventId);
  if (eventKey === null)
    throw new RunSheetError('event', 'Die Basar-ID hat nicht die erwartete Form.');
  if (!isRunSheetId(input.id))
    throw new RunSheetError('id', 'Die Kennung hat nicht die erwartete Form.');
  if (input.codes.length === 0)
    throw new RunSheetError('empty', 'Ein Laufzettel braucht mindestens einen Code.');
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const raw of input.codes) {
    const classified = classifyScannedCode(raw);
    if (classified === null) throw new RunSheetError('code', 'Ein Code hat keine gültige Form.');
    if (seen.has(classified.code))
      throw new RunSheetError('duplicate', 'Ein Code steht doppelt in der Liste.');
    seen.add(classified.code);
    codes.push(classified.code);
  }
  const groups: string[][] = [];
  let current: string[] = [];
  for (const code of codes) {
    const candidate = [...current, code];
    if (
      current.length > 0 &&
      (candidate.length > RUN_SHEET_MAX_CODES || partLength(candidate) > RUN_SHEET_MAX_CHARS)
    ) {
      groups.push(current);
      current = [code];
    } else {
      current = candidate;
    }
  }
  groups.push(current);
  if (groups.length > RUN_SHEET_MAX_PARTS) {
    throw new RunSheetError('too_many', 'Zu viele Artikel für einen Laufzettel.');
  }
  return groups.map((group, index) =>
    partText(eventKey, input.id, index + 1, groups.length, group),
  );
}

// --- Lesen -------------------------------------------------------------------------------------------------------------

export interface RunSheetPart {
  eventKey: string;
  id: string;
  /** Teilnummer ab 1 */
  part: number;
  parts: number;
  /** Codes in der Schreibweise von `classifyScannedCode` (BARSO-Code 10 Ziffern, fremder Code in Großbuchstaben). */
  codes: string[];
}

/** Sieht der Text nach einem Laufzettel aus (Präfix und Leerzeichen; ein Code kann kein Leerzeichen enthalten)? Sagt nichts darüber, ob er heil ist: dafür `parseRunSheetPart`. */
export function isRunSheetText(text: string): boolean {
  return text.trim().toUpperCase().startsWith(`${RUN_SHEET_PREFIX} `);
}

/**
 * Liest einen Teil. `null` bei allem, was nicht genau stimmt: Präfix, Aufbau, Teilzahl, Anzahl gegen gelesene Codes, Länge,
 * Prüfwert, ein Code, den `classifyScannedCode` nicht in genau dieser Schreibweise ausgibt, doppelte Codes. Kleinbuchstaben
 * werden vor der Prüfung großgeschrieben (Feststelltaste am Handscanner); alles andere zählt streng.
 */
export function parseRunSheetPart(text: string): RunSheetPart | null {
  const upper = text.trim().toUpperCase();
  // Nur druckbares ASCII: alles andere ist kein Laufzettel (und würde die SHA-256-Rechnung abbrechen)
  if (/[^\x20-\x7E]/.test(upper)) return null;
  if (upper.length > RUN_SHEET_MAX_CHARS) return null;
  const fields = upper.split(' ');
  // Präfix, Basar, Kennung, Teil, Anzahl, mindestens ein Code, Prüfwert
  if (fields.length < 7 || fields[0] !== RUN_SHEET_PREFIX) return null;
  const [, eventKey = '', id = '', partField = '', countField = ''] = fields;
  const check = fields[fields.length - 1] ?? '';
  const codes = fields.slice(5, -1);
  if (!EVENT_KEY_PATTERN.test(eventKey) || !ID_PATTERN.test(id) || !CHECK_PATTERN.test(check))
    return null;
  const partMatch = /^([1-9])\.([1-9])$/.exec(partField);
  if (partMatch === null) return null;
  const part = Number(partMatch[1]);
  const parts = Number(partMatch[2]);
  if (part > parts || parts > RUN_SHEET_MAX_PARTS) return null;
  if (!/^[1-9]\d?$/.test(countField)) return null;
  const count = Number(countField);
  if (count !== codes.length || count > RUN_SHEET_MAX_CODES) return null;
  if (runSheetChecksum(fields.slice(0, -1).join(' ')) !== check) return null;
  const seen = new Set<string>();
  for (const code of codes) {
    const classified = classifyScannedCode(code);
    // Streng: Der Text enthält genau die Schreibweise, die die Funktion ausgibt (keine Bindestriche in Ziffernfolgen usw.)
    if (classified?.code !== code || seen.has(code)) return null;
    seen.add(code);
  }
  return { eventKey, id, part, parts, codes };
}

// --- Zusammenführen an der Kasse ---------------------------------------------------------------------------------------

/** Die bisher gelesenen Teile eines Laufzettels (eine Kennung). */
export interface RunSheetAssembly {
  eventKey: string;
  id: string;
  parts: number;
  /** Gelesene Teile nach Teilnummer. */
  received: ReadonlyMap<number, readonly string[]>;
}

export type RunSheetAddResult =
  | { kind: 'added'; assembly: RunSheetAssembly }
  /** Dieser Teil war schon gelesen (gleicher Inhalt): nichts ändert sich, nichts wird doppelt gezählt. */
  | { kind: 'repeat'; assembly: RunSheetAssembly }
  /** Teil eines anderen Korbs (andere Kennung): Teile zweier Laufzettel mischen sich nie. */
  | { kind: 'other_sheet'; assembly: RunSheetAssembly }
  /** Gleiche Kennung, aber nicht zusammenpassend (andere Teilzahl oder anderer Inhalt für dieselbe Teilnummer). */
  | { kind: 'inconsistent'; assembly: RunSheetAssembly };

/** Fügt einen gelesenen Teil hinzu. `current = null`: der Teil beginnt einen neuen Laufzettel. */
export function addRunSheetPart(
  current: RunSheetAssembly | null,
  part: RunSheetPart,
): RunSheetAddResult {
  if (current === null) {
    return {
      kind: 'added',
      assembly: {
        eventKey: part.eventKey,
        id: part.id,
        parts: part.parts,
        received: new Map([[part.part, part.codes]]),
      },
    };
  }
  if (part.id !== current.id || part.eventKey !== current.eventKey) {
    return { kind: 'other_sheet', assembly: current };
  }
  if (part.parts !== current.parts) return { kind: 'inconsistent', assembly: current };
  const known = current.received.get(part.part);
  if (known !== undefined) {
    const same =
      known.length === part.codes.length && known.every((code, i) => code === part.codes[i]);
    return same
      ? { kind: 'repeat', assembly: current }
      : { kind: 'inconsistent', assembly: current };
  }
  // Ein Code in zwei Teilen derselben Kennung wäre ein Widerspruch (die Handy-Seite erzeugt das nie)
  const all = new Set([...current.received.values()].flat());
  if (part.codes.some((code) => all.has(code))) return { kind: 'inconsistent', assembly: current };
  return {
    kind: 'added',
    assembly: {
      ...current,
      received: new Map([...current.received, [part.part, part.codes]]),
    },
  };
}

/** Teilnummern, die noch fehlen (aufsteigend); leer = vollständig. */
export function missingRunSheetParts(assembly: RunSheetAssembly): number[] {
  const missing: number[] = [];
  for (let part = 1; part <= assembly.parts; part += 1) {
    if (!assembly.received.has(part)) missing.push(part);
  }
  return missing;
}

/** Alle Codes in der Reihenfolge der Teile; `null`, solange Teile fehlen. */
export function completeRunSheetCodes(assembly: RunSheetAssembly): string[] | null {
  if (missingRunSheetParts(assembly).length > 0) return null;
  const codes: string[] = [];
  for (let part = 1; part <= assembly.parts; part += 1)
    codes.push(...(assembly.received.get(part) ?? []));
  return codes;
}
